import React, { ReactElement, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'

import { AISearchIcon, ChevronDownIcon, ChevronUpIcon } from '~assets/icons'
import { Chunk, Page } from '~types'

interface RelevantChunksDebugInfoProps {
  relevantChunks: Chunk[]
}

export const RelevantChunksDebugInfo = ({
  relevantChunks,
}: RelevantChunksDebugInfoProps): ReactElement => {
  const [showSources, setShowSources] = React.useState<boolean>(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [selectedChunk, setSelectedChunk] = useState<Chunk | null>(null)

  // Function to truncate text to avoid overflow issues
  const truncateText = (text: string, maxLength: number = 150): string => {
    if (!text) return ''
    // Remove any HTML-like content
    const cleanText = text.replace(/<[^>]*>/g, '')
    return cleanText.length > maxLength ? `${cleanText.substring(0, maxLength)}...` : cleanText
  }

  // Function to get a clean title from the page
  const getPageTitle = (page: Page | null | undefined): string => {
    if (!page || !page.title) return 'Untitled Source'
    return page.title.startsWith('http') ? new URL(page.title).hostname : page.title
  }

  const handleCopyChunkId = (id: string): void => {
    navigator.clipboard.writeText(id)
    setCopiedId(id)
    setTimeout(() => {
      setCopiedId(null)
    }, 2000)
  }

  const handleOpenDialog = (chunk: Chunk): void => {
    setSelectedChunk(chunk)
    setDialogOpen(true)
  }

  const handleCloseDialog = (): void => {
    setDialogOpen(false)
    setSelectedChunk(null)
  }

  const handleCopyFullText = (): void => {
    if (selectedChunk && selectedChunk.text) {
      navigator.clipboard.writeText(selectedChunk.text)
    }
  }

  return (
    <Box padding={1} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }} tabIndex={-1}>
      <Box
        onClick={() => setShowSources(!showSources)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mr: 2,
        }}
      >
        <AISearchIcon height={15} width={15} />
        <Typography
          variant='caption'
          sx={{ ml: 1, color: 'text.secondary', fontWeight: 'bold', fontSize: 14 }}
        >
          Retrieval Info:
        </Typography>
        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
          {showSources ? (
            <Box ml={1}>
              <ChevronDownIcon color={'var(--steel)'} />
            </Box>
          ) : (
            <ChevronUpIcon color={'var(--steel)'} />
          )}
        </Box>
      </Box>

      {showSources && (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ mt: 1 }}>
            {relevantChunks && relevantChunks.length > 0 ? (
              relevantChunks.map((chunk, index) => (
                <Box
                  key={chunk.id || index}
                  sx={{
                    mb: 2,
                    p: 1,
                    borderLeft: '2px solid #eaeaea',
                    pl: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant='body2' sx={{ fontWeight: 'bold', mr: 1 }}>
                      {index + 1}.
                    </Typography>
                    {chunk.page && chunk.page.url ? (
                      <Link
                        href={chunk.page.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{ fontWeight: 'medium' }}
                      >
                        {getPageTitle(chunk.page)}
                      </Link>
                    ) : (
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        {chunk.text}
                      </Typography>
                    )}
                    {chunk.order !== undefined && chunk.order !== null && (
                      <Chip
                        size='small'
                        label={`Chunk #${chunk.order}`}
                        color='default'
                        sx={{ ml: 2, height: 20 }}
                      />
                    )}
                  </Box>

                  {chunk.id && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 3,
                        mb: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        p: 0.5,
                        pl: 1,
                        borderRadius: 1,
                        width: 'fit-content',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'monospace',
                          mr: 1,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => handleOpenDialog(chunk)}
                      >
                        ID: {chunk.id}
                      </Typography>
                      <Tooltip title={copiedId === chunk.id ? 'Copied!' : 'Copy Chunk ID'}>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopyChunkId(chunk.id || '')
                          }}
                          sx={{
                            padding: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            },
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      fontSize: '0.8rem',
                      mt: 0.5,
                      ml: 3,
                    }}
                  >
                    {truncateText(chunk.text)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No sources retrieved
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
        aria-labelledby='chunk-raw-text-dialog'
      >
        <DialogTitle id='chunk-raw-text-dialog'>
          Raw Chunk Text
          {selectedChunk && selectedChunk.id && (
            <Typography
              variant='caption'
              sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}
            >
              ID: {selectedChunk.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: 1,
              p: 2,
            }}
          >
            {selectedChunk && selectedChunk.text ? selectedChunk.text : 'No text available'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyFullText} startIcon={<ContentCopyIcon />}>
            Copy Full Text
          </Button>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
